import React, { useMemo } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { AgentAvatar } from '@atlaskit/rovo-agent-components';
import { getAgentAvatarColor } from '@atlaskit/rovo-agent-components/ui/agent-avatar/GeneratedAvatar';
import { token } from '@atlaskit/tokens';

import { isForgeAgent } from '@atlassian/conversation-assistant-agent';
import { normalizeAccountIdFromIdentityAri } from '@atlassian/conversation-assistant-utils/default-agent';

import { AgentRowAvatar } from '@confluence/agent-user-dual-avatar/entry-points/AgentRowAvatar.ts';
import type {
	PresenceHexOutline,
	RovoPresenceColorMode,
} from '@confluence/agent-user-dual-avatar/entry-points/AgentRowAvatar.ts';
import { useAgentAvatarColorMode } from '@confluence/agent-user-dual-avatar/entry-points/useAgentAvatarColorMode.ts';
import { fg } from '@confluence/feature-gating';

import {
	AGENT_AVATAR_SHELL_PATH,
	AGENT_AVATAR_SHELL_SIZE,
	AGENT_AVATAR_SHELL_VIEWBOX,
	AgentPresenceOutline,
} from '../shared/AgentPresenceOutline.tsx';
import type { AgentAvatarSize } from '../shared/AgentPresenceOutline.tsx';
import type { AgentWithPresence } from './AgentPresenceTypes.ts';
import { AgentIdentityAvatar } from './AgentIdentityAvatar.tsx';
import { AgentPresenceAvatarFrame } from './AgentPresenceAvatarFrame.tsx';

type PresenceArtworkOptions = {
	borderColor: string;
	colorMode: RovoPresenceColorMode;
	presenceTileFill?: string;
	presenceOutline?: PresenceHexOutline;
};

const AGENT_AVATAR_SHELL_BORDER_WIDTH = 1;
const styles = cssMap({
	avatarContent: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
	},
	shell: {
		display: 'block',
		position: 'absolute',
		left: '50%',
		pointerEvents: 'none',
	},
	legacyBorder: {
		position: 'relative',
		filter: `drop-shadow(1px 0px 0px ${token('color.icon.inverse')}) drop-shadow(-1px 0px 0px ${token('color.icon.inverse')}) drop-shadow(0px 1px 0px ${token('color.icon.inverse')}) drop-shadow(0px -1px 0px ${token('color.icon.inverse')})`,
	},
});

const AgentAvatarShell = ({
	backgroundColor,
	borderColor,
	size,
}: {
	backgroundColor: string;
	borderColor: string;
	size: AgentAvatarSize;
}): React.JSX.Element => {
	const { width, height } = AGENT_AVATAR_SHELL_SIZE[size];

	return (
		<Box
			as="span"
			xcss={styles.shell}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Exposes the exact fractional shell offset, rotation, and opacity required by the design specification.
			style={{ top: '0.2px', transform: 'translateX(-50%) rotate(0deg)', opacity: 1 }}
			testId="agent-presence-avatar-shell"
		>
			<svg
				data-testid="agent-presence-avatar-shell-svg"
				display="block"
				width={width}
				height={height}
				viewBox={AGENT_AVATAR_SHELL_VIEWBOX[size]}
				overflow={size === 'small' ? 'visible' : undefined}
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				aria-hidden="true"
				focusable="false"
			>
				<path
					data-testid="agent-presence-avatar-shell-shape"
					d={AGENT_AVATAR_SHELL_PATH[size]}
					fill={backgroundColor}
					stroke={borderColor}
					strokeWidth={AGENT_AVATAR_SHELL_BORDER_WIDTH}
					vectorEffect="non-scaling-stroke"
				/>
			</svg>
		</Box>
	);
};

const getShellColors = (
	agent: AgentWithPresence,
	borderColor?: string,
): { backgroundColor: string; borderColor: string } => {
	const { primary } = getAgentAvatarColor({
		agentIdentityAccountId: agent.identity_account_id,
		agentId: agent.id,
		agentNamedId: agent.external_config_reference ?? agent.named_id,
	});

	return { backgroundColor: primary, borderColor: borderColor ?? primary };
};

const isNormallyWhiteBrand = (agentIcon: AgentWithPresence['agentIcon']): boolean => {
	switch (agentIcon) {
		case 'antigravity':
		case 'codex':
		case 'gemini':
		case 'hermes':
		case 'microsoft-copilot':
		case 'vscode':
			return true;
		default:
			return false;
	}
};

export type AgentPresenceAvatarProps = {
	agent: AgentWithPresence;
	isOverlapped?: boolean;
	stackIndex?: number;
	showBorder?: boolean;
	size?: AgentAvatarSize;
};

export function AgentPresenceAvatar({
	agent,
	isOverlapped,
	stackIndex,
	showBorder,
	size = 'medium',
}: AgentPresenceAvatarProps): React.JSX.Element {
	const colorMode = useAgentAvatarColorMode();
	let isAgentPresenceMoveEnabled = false;
	// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
	if (fg('platform_move_presence_agents')) {
		isAgentPresenceMoveEnabled = true;
	}
	const isRefinedGeometryEnabled =
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		isAgentPresenceMoveEnabled && fg('confluence_refined_agent_presence_geometries');
	const isFacepileAvatar = isRefinedGeometryEnabled && stackIndex !== undefined;
	const usesSharedFacepileOutline =
		isFacepileAvatar &&
		(!agent.agentIcon ||
			agent.agentIcon === 'open-code' ||
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			!fg('agentic_presence_facepile_master_gate'));
	const showArtworkOutline =
		!usesSharedFacepileOutline && (!isFacepileAvatar || Boolean(showBorder));
	const usesSquareFacepileViewport =
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		isFacepileAvatar && size === 'small' && fg('agentic_presence_facepile_master_gate');
	const isWhiteBrand = isNormallyWhiteBrand(agent.agentIcon);
	const centerStrokeColor =
		isRefinedGeometryEnabled && colorMode === 'light' && isWhiteBrand
			? token('color.background.accent.gray.subtler')
			: undefined;
	const presenceOutline = useMemo<PresenceHexOutline | undefined>(
		() =>
			isRefinedGeometryEnabled
				? {
						width: showArtworkOutline ? 2 : 0,
						svgHeight: usesSquareFacepileViewport ? 24 : size === 'small' ? 27 : 36,
						centerStrokeColor,
						centerStrokeWidth: centerStrokeColor ? 1 : undefined,
					}
				: undefined,
		[
			isRefinedGeometryEnabled,
			showArtworkOutline,
			usesSquareFacepileViewport,
			size,
			centerStrokeColor,
		],
	);
	const renderAgentAvatar = (
		avatarSize: AgentAvatarSize,
		presenceArtwork?: PresenceArtworkOptions,
	) =>
		agent.agentIcon ? (
			<AgentRowAvatar
				name={agent.name}
				agentIcon={agent.agentIcon}
				avatarSize={avatarSize}
				hasInlineSpacing={false}
				colorMode={presenceArtwork?.colorMode}
				borderColor={presenceArtwork?.borderColor}
				presenceTileFill={presenceArtwork?.presenceTileFill}
				presenceOutline={presenceArtwork?.presenceOutline}
				usePresenceArtwork={presenceArtwork ? true : undefined}
			/>
		) : (
			<AgentAvatar
				key={agent.id}
				imageUrl={agent.avatarUrl}
				agentId={agent.id}
				agentIdentityAccountId={agent.identity_account_id}
				agentNamedId={agent.external_config_reference ?? agent.named_id}
				size={avatarSize}
				showBorder={false}
				isForgeAgent={isForgeAgent(agent)}
				forgeAgentIconUrl={agent.icon}
			/>
		);

	if (!isAgentPresenceMoveEnabled) {
		return (
			<Box
				xcss={cx(showBorder && styles.legacyBorder)}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Restores the legacy runtime z-index used by the standalone agent avatar group.
				style={{ zIndex: stackIndex }}
			>
				{renderAgentAvatar('medium')}
			</Box>
		);
	}

	const accountId = normalizeAccountIdFromIdentityAri(agent.identity_account_id);
	if (
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning -- This patch must only run within the enabled facepile rollout.
		isFacepileAvatar &&
		!agent.agentIcon &&
		!agent.is_default &&
		accountId &&
		fg('agentic_presence_facepile_master_gate') &&
		fg('cc_editor_agent_presence_patch_1')
	) {
		return (
			<AgentIdentityAvatar
				accountId={accountId}
				name={agent.name}
				size={size}
				showBorder={showBorder}
				stackIndex={stackIndex}
				isOverlapped={isOverlapped}
			/>
		);
	}

	const avatarSize = size;
	let presenceArtwork: PresenceArtworkOptions | undefined;
	if (isRefinedGeometryEnabled) {
		const borderColor = showArtworkOutline ? token('elevation.surface') : 'transparent';
		const presenceTileFill =
			colorMode === 'dark' && isWhiteBrand
				? token('color.background.accent.gray.subtler')
				: undefined;
		presenceArtwork = { borderColor, colorMode, presenceTileFill, presenceOutline };
	}
	const shellBorderColor = usesSharedFacepileOutline
		? 'transparent'
		: isRefinedGeometryEnabled
			? colorMode === 'dark'
				? 'transparent'
				: token('elevation.surface')
			: undefined;
	const shellColors =
		showBorder && !agent.agentIcon ? getShellColors(agent, shellBorderColor) : undefined;
	const agentAvatar = renderAgentAvatar(avatarSize, presenceArtwork);

	return (
		<AgentPresenceAvatarFrame
			size={showBorder ? avatarSize : undefined}
			isBlock={
				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
				showBorder && fg('confluence_neutral_human_presence_avatars')
			}
			isOverlapped={isOverlapped}
			stackIndex={stackIndex}
		>
			{showBorder && usesSharedFacepileOutline ? (
				<AgentPresenceOutline color={token('elevation.surface')} size={avatarSize} />
			) : null}
			{showBorder && shellColors ? (
				<>
					<AgentAvatarShell {...shellColors} size={avatarSize} />
					<Box as="span" xcss={styles.avatarContent}>
						{agentAvatar}
					</Box>
				</>
			) : usesSharedFacepileOutline ? (
				<Box as="span" xcss={styles.avatarContent}>
					{agentAvatar}
				</Box>
			) : (
				agentAvatar
			)}
		</AgentPresenceAvatarFrame>
	);
}
